import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { adminApi, errMsg } from "@/lib/api";
import { PageTitle, Btn, Panel } from "@/admin/ui";
import { AreaDemandPanel } from "@/admin/AreaDemandPanel";
import { WaitlistPanel } from "@/admin/WaitlistPanel";
import { NotifyWaitlistButton } from "@/admin/NotifyWaitlist";

const AreaRow = ({ area, onSaved, waitlist }) => {
  const [f, setF] = useState({ min_order: area.min_order || 0, delivery_fee: area.delivery_fee || 0 });
  const dirty = Number(f.min_order) !== (area.min_order || 0) || Number(f.delivery_fee) !== (area.delivery_fee || 0);
  const put = async (extra = {}) => {
    try {
      await adminApi.put(`/areas/${area.id}`, { pincode: area.pincode, name: area.name, active: area.active, min_order: Number(f.min_order) || 0, delivery_fee: Number(f.delivery_fee) || 0, ...extra });
      toast.success("Zone updated"); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => { if (window.confirm(`Remove ${area.pincode}?`)) { await adminApi.delete(`/areas/${area.id}`); onSaved(); } };
  return (
    <div className="grid grid-cols-2 md:grid-cols-[80px_1fr_110px_110px_auto_auto_auto_auto] gap-2 items-center p-3" data-testid={`admin-area-${area.pincode}`}>
      <span className="font-bold text-primary">{area.pincode}</span>
      <span className="text-sm">{area.name}</span>
      <label className="text-[11px] text-stone-500">Min order ₹<Input type="number" min="0" value={f.min_order} onChange={(e) => setF({ ...f, min_order: e.target.value })} className="h-8" data-testid={`area-min-${area.pincode}`} /></label>
      <label className="text-[11px] text-stone-500">Delivery fee ₹<Input type="number" min="0" value={f.delivery_fee} onChange={(e) => setF({ ...f, delivery_fee: e.target.value })} className="h-8" data-testid={`area-fee-${area.pincode}`} /></label>
      <Btn disabled={!dirty} onClick={() => put()} data-testid={`area-save-${area.pincode}`}>Save</Btn>
      <Switch checked={area.active} onCheckedChange={(v) => put({ active: v })} data-testid={`area-active-${area.pincode}`} />
      <NotifyWaitlistButton area={area} waitlist={waitlist} onDone={onSaved} />
      <button onClick={del} className="p-1.5 text-stone-500 hover:text-accent" data-testid={`area-delete-${area.pincode}`}><Trash2 className="w-4 h-4" /></button>
    </div>
  );
};

export default function AdminAreas() {
  const [areas, setAreas] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [f, setF] = useState({ pincode: "", name: "", min_order: "", delivery_fee: "" });
  const load = () => {
    adminApi.get("/areas").then((r) => setAreas(r.data));
    setTimeout(() => adminApi.get("/waitlist").then((r) => setWaitlist(r.data)), 800);
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post("/areas", { pincode: f.pincode, name: f.name, active: true, min_order: Number(f.min_order) || 0, delivery_fee: Number(f.delivery_fee) || 0 });
      setF({ pincode: "", name: "", min_order: "", delivery_fee: "" }); toast.success("Pincode added"); load();
    } catch (er) { toast.error(errMsg(er)); }
  };

  return (
    <div className="max-w-4xl">
      <PageTitle sub="Only these pincodes can order. Set a minimum order value and delivery fee per zone (0 = none / free).">Serviceable pincodes</PageTitle>
      <Panel title="Add zone" className="mb-4">
        <form onSubmit={add} className="flex flex-wrap gap-2">
          <Input placeholder="Pincode" value={f.pincode} onChange={(e) => setF({ ...f, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} className="h-9 w-28" data-testid="area-pincode-input" />
          <Input placeholder="Area name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-9 flex-1 min-w-[160px]" data-testid="area-name-input" />
          <Input type="number" min="0" placeholder="Min order ₹" value={f.min_order} onChange={(e) => setF({ ...f, min_order: e.target.value })} className="h-9 w-32" data-testid="area-min-input" />
          <Input type="number" min="0" placeholder="Delivery fee ₹" value={f.delivery_fee} onChange={(e) => setF({ ...f, delivery_fee: e.target.value })} className="h-9 w-32" data-testid="area-fee-input" />
          <Btn disabled={f.pincode.length !== 6 || f.name.length < 2} data-testid="area-add-btn">Add</Btn>
        </form>
      </Panel>
      <div className="bg-white border border-stone-200 rounded-md divide-y divide-stone-100">
        {areas.map((a) => <AreaRow key={`${a.id}-${a.min_order}-${a.delivery_fee}-${a.active}`} area={a} onSaved={load} waitlist={waitlist} />)}
      </div>
      <WaitlistPanel waitlist={waitlist} />
      <AreaDemandPanel />
    </div>
  );
}
